/// <amd-module name="Wit/SprintsHub/SprintsView/Components/SprintHeader/SprintsTeachingBubbles" />
import * as React from "react";
import { ObservableValue } from "VSS/Core/Observable";
import { isFeatureFlagEnabled } from "VSS/Platform/Feature";
import { usePageContext } from "VSS/Platform/Layout";
import { ISettingsService, SettingsUserScope } from "VSS/Platform/Settings";
import { Observer } from "VSSUI/Observer";
import { TeachingBubble, TeachingBubbleCornerPlacement } from "VSSUI/TeachingBubble";
import { Location } from "VSSUI/Utilities/Position";
import { FeatureFlags } from "Wit/Common/Constants/FeatureFlags";
import { SettingsConstants } from "Wit/SprintsHub/Common/Constants";
import * as Resources from "Wit/SprintsHub/SprintsView/Resources";

interface ISprintsTeachingBubblesProps {
    showColumnOptionsBubble: ObservableValue<boolean>;
    showIterationPickerBubble: ObservableValue<boolean>;
    showViewOptionsBubble: ObservableValue<boolean>;
}

export function SprintsTeachingBubbles({ showColumnOptionsBubble, showIterationPickerBubble, showViewOptionsBubble }: ISprintsTeachingBubblesProps) {
    const pageContext = usePageContext();
    const isNewSprintsViewEnabled = isFeatureFlagEnabled(pageContext, FeatureFlags.NewSprintsBacklogsView, false);

    const setTeachingBubbleSetting = (settingsKey: string) => {
        const settingsService = pageContext.getService<ISettingsService>("ISettingsService");
        settingsService.setEntries(
            {
                [settingsKey]: true
            },
            SettingsUserScope.Me
        );
    };

    const onColumnOptionsBubbleClosed = () => {
        showColumnOptionsBubble.value = false;
        setTeachingBubbleSetting(`${SettingsConstants.Taskboard}/${SettingsConstants.ColumnOptionsBubbleSetting}`);
    };

    const onIterationPickerBubbleClosed = () => {
        showIterationPickerBubble.value = false;
        setTeachingBubbleSetting(`${SettingsConstants.SprintsHub}/${SettingsConstants.IterationPickerBubbleSetting}`);
    };

    const onViewOptionsBubbleClosed = () => {
        showViewOptionsBubble.value = false;
        setTeachingBubbleSetting(`${SettingsConstants.RightPane}/${SettingsConstants.ViewOptionsBubbleSetting}`);
    };

    return (
        <>
            <Observer showColumnOptionsBubble={showColumnOptionsBubble}>
                {(observerProps: { showColumnOptionsBubble: boolean }) => {
                    return observerProps.showColumnOptionsBubble && document.getElementsByClassName("taskboard-column-options").length > 0 ? (
                        <TeachingBubble
                            anchorElement={document.getElementsByClassName("taskboard-column-options")[0] as HTMLElement}
                            anchorOrigin={{
                                horizontal: Location.end,
                                vertical: Location.end
                            }}
                            cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                            onDismiss={onColumnOptionsBubbleClosed}
                            primaryButtonProps={{
                                text: Resources.GotIt,
                                primary: true,
                                onClick: onColumnOptionsBubbleClosed
                            }}
                            text={Resources.ColumnOptionsTeachingBubbleText}
                            title={Resources.ColumnOptions}
                        />
                    ) : null;
                }}
            </Observer>
            { isNewSprintsViewEnabled ? (
                <Observer showIterationPickerBubble={showIterationPickerBubble}>
                    {(observerProps: { showIterationPickerBubble: boolean }) => {
                        return observerProps.showIterationPickerBubble &&
                            document.getElementsByClassName("sprint-picker-dropdown").length > 0 ? (
                            <TeachingBubble
                                anchorElement={document.getElementsByClassName("sprint-picker-dropdown")[0] as HTMLElement}
                                anchorOrigin={{
                                    horizontal: Location.start,
                                    vertical: Location.end
                                }}
                                cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                                onDismiss={onIterationPickerBubbleClosed}
                                primaryButtonProps={{
                                    text: Resources.GotIt,
                                    primary: true,
                                    onClick: onIterationPickerBubbleClosed
                                }}
                                text={Resources.SprintPickerTeachingBubbleText}
                                title={Resources.SprintPicker}
                            />
                        ) : null;
                    }}
                </Observer>
            ) : (
                <Observer showIterationPickerBubble={showIterationPickerBubble}>
                    {(observerProps: { showIterationPickerBubble: boolean }) => {
                        return observerProps.showIterationPickerBubble &&
                            document.getElementsByClassName("bolt-dropdown-expandable-text-field").length > 0 ? (
                            <TeachingBubble
                                anchorElement={document.getElementsByClassName("bolt-dropdown-expandable-text-field")[0] as HTMLElement}
                                anchorOrigin={{
                                    horizontal: Location.end,
                                    vertical: Location.end
                                }}
                                cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                                onDismiss={onIterationPickerBubbleClosed}
                                primaryButtonProps={{
                                    text: Resources.GotIt,
                                    primary: true,
                                    onClick: onIterationPickerBubbleClosed
                                }}
                                text={Resources.SprintPickerTeachingBubbleText}
                                title={Resources.SprintPicker}
                            />
                        ) : null;
                    }}
                </Observer>
            )}
            <Observer showViewOptionsBubble={showViewOptionsBubble}>
                {(observerProps: { showViewOptionsBubble: boolean }) => {
                    return observerProps.showViewOptionsBubble && document.getElementsByClassName("sprints-view-options").length > 0 ? (
                        <TeachingBubble
                            anchorElement={document.getElementsByClassName("sprints-view-options")[0] as HTMLElement}
                            anchorOrigin={{
                                horizontal: Location.end,
                                vertical: Location.end
                            }}
                            cornerPlacement={TeachingBubbleCornerPlacement.vertical}
                            onDismiss={onViewOptionsBubbleClosed}
                            primaryButtonProps={{
                                text: Resources.GotIt,
                                primary: true,
                                onClick: onViewOptionsBubbleClosed
                            }}
                            text={Resources.PaneClosedTeachingBubbleText}
                            title={Resources.PanelClosed}
                        />
                    ) : null;
                }}
            </Observer>
        </>
    );
}
